import React from "react";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { MONTHS, DOW, dateStr, daysInMonth, mondayDow } from "./dateUtils";

// The calendar card: search bar, month navigation, weekday header, the
// 7-column day grid with activity dots, and the dot legend.
export default function CalendarGrid({
    query, setQuery,
    cursor, goPrev, goNext,
    todayKey, selectedDay, onSelectDay,
    matchDates, hasActiveQuery,
    tasksByDate, remindersByDate, habitsForDate,
}) {
    const dim = daysInMonth(cursor.y, cursor.m);
    const firstDow = mondayDow(new Date(cursor.y, cursor.m, 1));
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) cells.push(d);

    return (
        <div className="bg-surface rounded-clay p-3 sm:p-4 shadow-clay">
            {/* Search bar */}
            <div className="flex items-center gap-2 bg-cream rounded-full pl-4 pr-1.5 py-1.5 mb-3 focus-within:ring-2 focus-within:ring-terracotta/40 transition-all">
                <Search size={14} className="text-warm-500 shrink-0" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search across all dates…"
                    className="flex-1 bg-transparent outline-none font-body text-sm text-warm-900 placeholder:text-warm-500 py-1"
                    data-testid="cal-search-input"
                    aria-label="Search across all dates"
                />
                {query && (
                    <button
                        onClick={() => setQuery("")}
                        className="w-7 h-7 rounded-full hover:bg-surface text-warm-500 hover:text-warm-900 flex items-center justify-center transition-all"
                        data-testid="cal-search-clear"
                        aria-label="Clear search"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            <div className="flex items-center justify-between mb-3">
                <button
                    onClick={goPrev}
                    className="w-9 h-9 rounded-full bg-cream hover:bg-cream/70 flex items-center justify-center text-warm-800 transition-all active:scale-95"
                    data-testid="cal-prev"
                    aria-label="Previous month"
                >
                    <ChevronLeft size={18} />
                </button>
                <h3 className="font-display text-base sm:text-lg text-warm-900" data-testid="cal-title">
                    {MONTHS[cursor.m]} {cursor.y}
                </h3>
                <button
                    onClick={goNext}
                    className="w-9 h-9 rounded-full bg-cream hover:bg-cream/70 flex items-center justify-center text-warm-800 transition-all active:scale-95"
                    data-testid="cal-next"
                    aria-label="Next month"
                >
                    <ChevronRight size={18} />
                </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1">
                {DOW.map((d, i) => (
                    <div key={`dow-${i}-${d}`} className="text-center font-display text-[10px] uppercase tracking-widest text-warm-500 py-1">{d}</div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                {cells.map((d, idx) => {
                    // `cells` is a derived array of 35-42 month grid cells,
                    // built fresh on every render in a fixed order. `idx`
                    // IS the stable identity for the leading padding cells;
                    // filled days key on their YYYY-MM-DD.
                    if (d == null) return <div key={`pad-${idx}`} />;
                    const ds = dateStr(cursor.y, cursor.m, d);
                    const isToday = ds === todayKey;
                    const isSel = ds === selectedDay;
                    const isMatch = hasActiveQuery && matchDates.has(ds);
                    const tList = tasksByDate[ds] || [];
                    const rList = remindersByDate[ds] || [];
                    const hList = habitsForDate(ds);
                    const hasTasks = tList.length > 0;
                    const hasReminders = rList.length > 0;
                    const hasHabits = hList.length > 0;
                    const allTaskDone = hasTasks && tList.every((t) => t.completed);
                    return (
                        <button
                            key={`day-${ds}`}
                            onClick={() => onSelectDay(ds)}
                            className={`relative aspect-square rounded-lg sm:rounded-xl flex flex-col items-center justify-center font-body transition-all active:scale-95
                                ${isSel
                                    ? "bg-terracotta text-cream shadow-clay"
                                    : isToday
                                    ? "bg-cream border-2 border-terracotta text-warm-900"
                                    : "bg-cream/60 hover:bg-cream text-warm-900"
                                }
                                ${isMatch && !isSel ? "ring-2 ring-mustard ring-offset-1 ring-offset-surface" : ""}`}
                            data-testid={`cal-day-${ds}`}
                        >
                            <span className={`text-xs sm:text-sm font-display ${isSel ? "" : "text-warm-900"}`}>{d}</span>
                            <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-0.5">
                                {hasTasks && (
                                    <span className={`w-1.5 h-1.5 rounded-full ${
                                        isSel ? "bg-cream/90" : allTaskDone ? "bg-sage" : "bg-terracotta"
                                    }`} />
                                )}
                                {hasHabits && (
                                    <span className={`w-1.5 h-1.5 rounded-full ${isSel ? "bg-cream/70" : "bg-mustard"}`} />
                                )}
                                {hasReminders && (
                                    <span className={`w-1.5 h-1.5 rounded-full ${isSel ? "bg-cream/50" : "bg-sage"}`} />
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="flex justify-center gap-3 mt-3 text-[10px] font-body text-warm-500">
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-terracotta" /> Tasks</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-mustard" /> Habits</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sage" /> Reminders</span>
            </div>
        </div>
    );
}
